/**
 * The export client — G2.4's "one shared export service", client side.
 *
 * Every table, grid, chart and tile in the product goes through `runExport`. There
 * is no second path, which is what makes the export census (G2 test d) meaningful:
 * a route either registers here or its numbers cannot leave the screen.
 */

import { DECIMAL_COMMA_SUFFIX, SYNCHRONOUS_ROW_LIMIT, isAbsent, type ExportAuditEvent, type ExportFormat, type ExportOutcome, type ExportRequest } from './types';

import { toCsvBlob } from './csv';
import { localTimeZone, toXlsxBlob, translated, wallClock, type XlsxOptions } from './xlsx';

export * from './types';
export { toCsvBlob, toCsvString, streamCsv, escapeField, serialiseCell, UTF8_BOM, CSV_SEPARATOR, CSV_BLOB_BATCH_CHARS } from './csv';
export { toXlsxBlob, toXlsxBuffer, buildXlsxWorkbook, planXlsx, sheetName, localTimeZone, SHEET_NAMES, XLSX_MIME } from './xlsx';
export { EXCEL_MAX_ROWS, SheetTooLargeError } from './xlsx-parts';
export { createByteSpool, type ByteSpool } from './spool';
export {
  arrangeDownloads, csvDialectForDecimal, csvFormatOf, decimalSeparator, otherCsvFormat, regionalCsvFormat,
  type CsvFormat, type DownloadArrangement, type DownloadButton,
} from './regional';
export { fileRow, type FileCell } from './table';
export {
  columnGuideRows, columnGuideCsv, columnGuideFilename, runColumnGuide, undescribedColumns, type ColumnGuideRow,
} from './column-guide';
export { columnMeaning, machineName, meaningOrGap } from './xlsx';
export {
  provenanceRows, fileProvenanceRows, missingProvenanceFields, assertComplete, IncompleteProvenanceError,
  NOT_PUBLISHED, NO_PRINCIPAL, PERIMETER_NOT_RECORDED, buildVersion,
} from './provenance';

export interface ExportRunOptions {
  readonly format: ExportFormat;
  readonly translate: XlsxOptions['translate'];
  readonly locale?: string;
  /** IANA zone the workbook's dates are written in; the browser's own by default (DEC-104). */
  readonly timeZone?: string;
  /**
   * Rows written so far, now and then, while the file is written: a large Excel workbook's rows on every sheet (`docs/adr/9073`),
   * a CSV's table rows (`docs/adr/9074`).
   */
  readonly onProgress?: (rows: number) => void;
  /**
   * Submits the request to the server's async job endpoint. G12: long operations
   * return 202 + a job resource, never a held connection. Injected so this module
   * has no knowledge of the API surface beyond the generated client.
   */
  readonly submitAsyncJob?: (request: ExportRequest, format: ExportFormat) => Promise<{ jobId: string; expiresAt: string }>;
  /**
   * G2.4 governance — "written to the audit log: who exported what, when, at what
   * perimeter". Injected; the server writes the record, the client states intent.
   */
  readonly audit?: (event: ExportAuditEvent) => void;
  readonly principalId?: string;
}

export class AsyncExportRequiredError extends Error {
  constructor(public readonly rowCount: number) {
    super(
      `${rowCount} rows exceeds the ${SYNCHRONOUS_ROW_LIMIT} synchronous limit and no `
      + 'submitAsyncJob handler was supplied. G2.1 budgets a synchronous export at 10 s '
      + 'up to 100k rows; above that G2.4 requires an asynchronous job with progress and '
      + 'notification, not a longer wait.',
    );
    this.name = 'AsyncExportRequiredError';
  }
}

/**
 * A format the product does not produce — ADR 9010 as amended on 2026-10-01 (DEC-98): every
 * export a planner takes is CSV and Excel (`xlsx`), the same table in both, and never a
 * picture. An image is refused here, in the one export service, so a screen that asks for one
 * fails loudly; the manifest type and the census refuse it earlier still. Parquet is not a
 * planner download — it is the server-side system extract — and is untouched.
 */
export class ExportFormatRefusedError extends Error {
  constructor(public readonly format: ExportFormat) {
    super(`Export format "${format}" is refused: exports are CSV and Excel, never an image (ADR 9010).`);
    this.name = 'ExportFormatRefusedError';
  }
}

const REFUSED_FORMATS: readonly ExportFormat[] = ['png', 'svg'];

/** Filename convention: `<id>-<hierarchy node>-<generated-at>.<ext>`, so two
 *  exports of the same screen at different perimeters never collide in Downloads. */
export function filenameFor(request: ExportRequest, format: ExportFormat): string {
  const stamp = request.provenance.generatedAt.replace(/[:.]/g, '-');
  // A declared absence has no node to name the file after (`docs/adr/9007`).
  const node = isAbsent(request.provenance.hierarchyNode)
    ? 'export'
    : request.provenance.hierarchyNode.replace(/[^\w-]+/g, '_');
  return `${request.id}-${node}-${stamp}.${format}`;
}

/** Characters no file system accepts in a name, and control characters. */
// eslint-disable-next-line no-control-regex
const UNSAFE_IN_FILENAME = /[\\/:*?"<>|\u0000-\u001f\u007f]+/g;

function filenamePart(value: string): string {
  return value.replace(UNSAFE_IN_FILENAME, ' ').trim().replace(/\s+/g, '-').replace(/^\.+|\.+$/g, '');
}

/** The run the file is bounded by, when its perimeter names one (`run_id equals …`). */
function runOf(request: ExportRequest): string | null {
  const run = request.perimeter.filters.find((filter) => filter.field === 'run_id' && filter.value !== '' && filter.operator !== 'unset');
  return run === undefined ? null : run.value;
}

/**
 * A downloaded file's name, for a person (DEC-104, `docs/adr/9041`; the CSV too since issue 249):
 * `<Title>_<market>_<run or local date>.<ext>` — «Trasferimenti-perché_CO_run-1a2b3c4d.xlsx», and
 * the same name with `.csv` for the CSV of the same export, so the two files of one download sit
 * side by side. The title is the export's own, translated; a title with no translation, or one
 * whose placeholder was left unfilled («…per {by}»), falls back to the export's id — never a raw
 * `{…}` in a file name. The market is the hierarchy node when one is stated; the run is the one the
 * perimeter names (its first eight characters, as the screens show it), otherwise the day the file
 * was generated, in the user's time zone. The decimal-comma CSV (`docs/adr/9072`) is the same stem
 * with `-decimal-comma` before `.csv`, so it sits beside the raw CSV rather than replacing it.
 */
export function exportFilename(
  request: ExportRequest, translate: XlsxOptions['translate'], timeZone: string, extension: 'csv' | 'xlsx', suffix = '',
): string {
  const titleKey = request.workbook?.titleKey ?? request.titleKey;
  const title = translated(translate, titleKey);
  const parts = [
    filenamePart(title ?? request.id),
    isAbsent(request.provenance.hierarchyNode) ? '' : filenamePart(request.provenance.hierarchyNode),
  ];
  const run = runOf(request);
  const generated = new Date(request.provenance.generatedAt);
  const day = Number.isNaN(generated.getTime()) ? new Date() : generated;
  parts.push(run === null ? wallClock(day, timeZone).toISOString().slice(0, 10) : `run-${filenamePart(run.slice(0, 8))}`);
  return `${parts.filter((part) => part !== '').join('_')}${suffix}.${extension}`;
}

/** The workbook's file name — {@link exportFilename} with `.xlsx`. */
export function workbookFilename(request: ExportRequest, translate: XlsxOptions['translate'], timeZone: string): string {
  return exportFilename(request, translate, timeZone, 'xlsx');
}

export async function runExport(request: ExportRequest, options: ExportRunOptions): Promise<ExportOutcome> {
  const { format } = options;
  if (REFUSED_FORMATS.includes(format)) throw new ExportFormatRefusedError(format);

  options.audit?.({
    exportId: request.id,
    principalId: options.principalId ?? 'unknown',
    requestedAt: new Date().toISOString(),
    format,
    rowCount: request.rowCount ?? null,
    perimeter: request.perimeter,
  });

  const rowCount = request.rowCount;
  if (rowCount !== undefined && rowCount > SYNCHRONOUS_ROW_LIMIT) {
    if (!options.submitAsyncJob) throw new AsyncExportRequiredError(rowCount);
    const { jobId, expiresAt } = await options.submitAsyncJob(request, format);
    return { mode: 'asynchronous', jobId, expiresAt };
  }

  const filename = filenameFor(request, format);

  switch (format) {
    case 'csv':
      // Named as the workbook is, in the reader's words (issue 249) — not the technical export id.
      return {
        mode: 'synchronous',
        blob: await toCsvBlob(request, 'standard', options.onProgress),
        filename: exportFilename(request, options.translate, options.timeZone ?? localTimeZone(), 'csv'),
      };
    case 'csv-decimal-comma':
      // The same file for Excel set to Italian or Spanish (owner ruling 2026-10-22, ADR 9072).
      return {
        mode: 'synchronous',
        blob: await toCsvBlob(request, 'decimal-comma', options.onProgress),
        filename: exportFilename(request, options.translate, options.timeZone ?? localTimeZone(), 'csv', DECIMAL_COMMA_SUFFIX),
      };
    case 'xlsx': {
      const timeZone = options.timeZone ?? localTimeZone();
      const xlsxOptions: XlsxOptions = {
        translate: options.translate,
        timeZone,
        ...(options.locale === undefined ? {} : { locale: options.locale }),
        ...(options.onProgress === undefined ? {} : { onProgress: options.onProgress }),
      };
      return {
        mode: 'synchronous',
        blob: await toXlsxBlob(request, xlsxOptions),
        filename: workbookFilename(request, options.translate, timeZone),
      };
    }
    case 'png':
    case 'svg': {
      if (!request.chart) {
        throw new Error(
          `Export "${request.id}" was asked for ${format.toUpperCase()} but declares no chart source. `
          + 'Image formats are only available on a chart surface; its table view exports as CSV/XLSX on the same path (G2.4).',
        );
      }
      return { mode: 'synchronous', blob: await request.chart.toImage(format), filename };
    }
    case 'parquet':
      // G2.4 lists Parquet for system-to-system extracts. It is produced
      // server-side from the same perimeter, never in the browser.
      if (!options.submitAsyncJob) throw new Error('Parquet extracts are produced server-side; supply submitAsyncJob.');
      return { mode: 'asynchronous', ...(await options.submitAsyncJob(request, format)) };
    default: {
      const exhaustive: never = format;
      throw new Error(`unsupported export format: ${String(exhaustive)}`);
    }
  }
}

/** Triggers the browser download for a synchronous outcome. */
export function saveOutcome(outcome: ExportOutcome): void {
  if (outcome.mode !== 'synchronous') return;
  const url = URL.createObjectURL(outcome.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = outcome.filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
